import {
  CheckCircleIcon,
  RankStarIcon,
  Typography,
  useFetchFile,
  UserInfoIcon,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { JobSeekerType, useUnssignJobToJobSeekerMutation } from "@graphql/generated";
import {
  Button,
  Card,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  User,
} from "@heroui/react";
import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import { UserSuggestionType } from "../index";
import { SuggestionDetailsModal } from "./SuggestionDetailsModal";
interface JobSeekersAwaitingListProps {
  jobSeekers?: UserSuggestionType[];
  onRefetch?: () => void;
}

const JobSeekersAwaitingList: React.FC<JobSeekersAwaitingListProps> = ({ jobSeekers, onRefetch }) => {
  const hasJobSeekers = !!jobSeekers?.length;
  const [selectedSuggestion, setSelectedSuggestion] = useState<UserSuggestionType | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [suggestionToUnassign, setSuggestionToUnassign] = useState<UserSuggestionType | null>(null);
  const [unassignJob, { loading: unassignLoading }] = useUnssignJobToJobSeekerMutation();

  const openDetailsModal = useCallback(
    (suggestion: UserSuggestionType) => {
      setSelectedSuggestion(suggestion);
      onOpen();
    },
    [onOpen],
  );

  const openConfirmModal = useCallback(
    (suggestion: UserSuggestionType) => {
      setSuggestionToUnassign(suggestion);
      onConfirmOpen();
    },
    [onConfirmOpen],
  );

  const handleUnassign = useCallback(() => {
    if (!suggestionToUnassign?.jobPositionAssignmentId) return;

    unassignJob({
      variables: {
        jobPositionAssignmentId: suggestionToUnassign.jobPositionAssignmentId,
      },
    }).then(() => {
      onRefetch?.();
      onConfirmClose();
    });
  }, [unassignJob, onRefetch, suggestionToUnassign, onConfirmClose]);

  return (
    <>
      {hasJobSeekers && (
        <div className="flex flex-col gap-4">
          <Typography className="text-sm font-semibold">
            Job Seekers Awaiting Approval ({jobSeekers?.length || 0}):
          </Typography>
          <div className="flex flex-row gap-4 flex-wrap">
            {jobSeekers?.map((userSuggestion, index) => {
              const jobSeeker = userSuggestion.user;

              return (
                <Card
                  key={index}
                  className="p-4 w-fit gap-3 flex flex-col justify-start items-start shadow-none border-1 border-zinc-200 rounded-lg"
                >
                  <UserCard jobSeeker={jobSeeker} />
                  <Divider />
                  <div className="flex justify-between items-end gap-1">
                    <RankStarIcon className="w-6 h-6" viewBox="0 0 28 28" stroke="#52525B" />
                    <Typography className="text-sm text-zinc-500">Score: </Typography>
                    <Typography className="text-sm font-bold">{jobSeeker?.profile?.score || "-"}</Typography>
                  </div>
                  <div className="flex justify-between items-center gap-1">
                    <CheckCircleIcon className="w-7 h-7 mt-1" viewBox="0 0 28 28" stroke="#52525B" />
                    <Typography className="text-sm text-zinc-500 ">Assigned at:</Typography>
                    <Typography className="text-sm font-bold ">
                      {dayjs(userSuggestion.createdAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </div>
                  <div className="flex justify-between items-center gap-1">
                    <UserInfoIcon className="w-7 h-7 mt-1" viewBox="0 0 28 28" stroke="#52525B" />
                    <Typography className="text-sm text-zinc-500 ">Resume:</Typography>
                    <ResumeLink jobSeeker={jobSeeker} />
                  </div>
                  <div className="flex flex-row gap-2 w-full">
                    <Button
                      color="primary"
                      variant="light"
                      onPress={() => openDetailsModal(userSuggestion)}
                      size="sm"
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    <Button
                      color="danger"
                      variant="light"
                      onPress={() => openConfirmModal(userSuggestion)}
                      size="sm"
                      className="flex-1"
                      isDisabled={unassignLoading && suggestionToUnassign?.suggestionId === userSuggestion.suggestionId}
                    >
                      {unassignLoading && suggestionToUnassign?.suggestionId === userSuggestion.suggestionId
                        ? "Unassigning..."
                        : "Unassign"}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <SuggestionDetailsModal isOpen={isOpen} onClose={onClose} suggestion={selectedSuggestion} />

      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose}>
        <ModalContent>
          <ModalHeader>Confirm Unassign</ModalHeader>
          <ModalBody>
            <Typography>
              Are you sure you want to unassign {suggestionToUnassign?.user?.firstName}{" "}
              {suggestionToUnassign?.user?.lastName} from this job position?
            </Typography>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onConfirmClose}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleUnassign} isLoading={unassignLoading}>
              Unassign
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default JobSeekersAwaitingList;

const ResumeLink: React.FC<{ jobSeeker: JobSeekerType }> = ({ jobSeeker }) => {
  const { fetchFile: fetchUserResumeBlob } = useFetchFile({
    urlPath: jobSeeker?.resume?.file.file,
    downloadMode: true,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <>
      {jobSeeker?.resume?.file.file ? (
        <div onClick={fetchUserResumeBlob} className="text-sm text-blue-500 cursor-pointer">
          {jobSeeker?.resume?.file.file?.split("/")?.at(-1)}
        </div>
      ) : (
        "-"
      )}
    </>
  );
};

const UserCard: React.FC<{ jobSeeker: JobSeekerType }> = ({ jobSeeker }) => {
  const { data: avatarUrl } = useFetchFile({
    urlPath: jobSeeker?.profile?.avatar?.file,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <User
      name={`${jobSeeker?.firstName && jobSeeker?.firstName} ${jobSeeker?.lastName && jobSeeker?.lastName}`}
      description={jobSeeker?.email}
      avatarProps={{
        src: avatarUrl,
        isDisabled: !avatarUrl,
        classNames: {
          img: "opacity-1",
        },
      }}
    />
  );
};
